import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import type * as Leaflet from 'leaflet';
import { UsuarioService } from 'src/app/base/services/usuario/usuario.service';

const TESELAS_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TESELAS_ATRIBUCION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>';
/** Un color por turno, en orden horario. */
const COLORES_TURNO = ['#1f6fd1', '#e8730c', '#6f42c1'];

/**
 * Reporte D del control de campo: el recorrido de un asesor en un día.
 *
 * Muestra los lugares que marcó, numerados en el orden en que llegó, unidos por una línea,
 * con el punto oficial de cada colegio y el recorrido del GPS automático de ese día (el
 * mismo de Usuarios → Seguimiento). Las marcas lejos del colegio o con error van en rojo.
 */
@Component({
  selector: 'app-mapa-recorrido-campo',
  templateUrl: './mapa-recorrido-campo.component.html',
  styleUrls: ['./mapa-recorrido-campo.component.css'],
  standalone: false
})
export class MapaRecorridoCampoComponent implements AfterViewInit, OnDestroy {

  /** Los asigna quien abre el modal: el asesor del reporte y un día calculado por el servidor. */
  id_empresa: number;
  asesor: any;
  dia: any;

  @ViewChild('contenedorMapa') contenedorMapa: ElementRef;

  /** Las visitas del día en orden, con su número y el color de su turno. */
  visitas: any[] = [];
  sinUbicacion = 0;
  posicionesGps = 0;
  cargandoGps = true;

  private L: typeof Leaflet;
  private mapa: Leaflet.Map;
  private marcadores: Leaflet.Marker[] = [];
  private capaGps: Leaflet.LayerGroup;

  constructor(public activeModal: NgbActiveModal,
    private usuarioService: UsuarioService,
    private zone: NgZone) { }

  async ngAfterViewInit() {
    this.armarVisitas();
    const leaflet = await import('leaflet');
    this.L = (window as any).L ?? leaflet;
    // Fuera de la zona: los eventos de mouse de Leaflet no tienen que disparar la detección de cambios.
    this.zone.runOutsideAngular(() => this.dibujar());
    this.cargarGps();
  }

  ngOnDestroy(): void {
    this.mapa?.remove();
  }

  private armarVisitas() {
    let numero = 0;
    this.visitas = [];
    this.dia.turnos.forEach((t: any, i: number) => {
      for (const v of t.visitas) {
        this.visitas.push(Object.assign({}, v, { numero: ++numero, turno: t.nombre, color: COLORES_TURNO[i % COLORES_TURNO.length] }));
      }
    });
    this.sinUbicacion = this.visitas.filter(v => v.latitud === null).length;
  }

  private dibujar() {
    const L = this.L;
    this.mapa = L.map(this.contenedorMapa.nativeElement, { center: [-17.3938, -66.1569], zoom: 13 });
    L.tileLayer(TESELAS_URL, { maxZoom: 19, attribution: TESELAS_ATRIBUCION }).addTo(this.mapa);
    this.capaGps = L.layerGroup().addTo(this.mapa);

    const puntos: Leaflet.LatLngExpression[] = [];
    const marcadas: [number, number][] = [];
    // Por turno, una línea con el orden de las llegadas.
    this.dia.turnos.forEach((t: any, i: number) => {
      const recorrido = this.visitas.filter(v => v.turno === t.nombre && v.latitud !== null).map(v => [v.latitud, v.longitud] as [number, number]);
      if (recorrido.length > 1) {
        L.polyline(recorrido, { color: COLORES_TURNO[i % COLORES_TURNO.length], weight: 3, opacity: 0.8, dashArray: '6 6' }).addTo(this.mapa);
      }
    });

    for (const v of this.visitas) {
      // El punto oficial del colegio, y una línea hasta donde marcó si quedó lejos.
      if (v.colegio_latitud !== null) {
        L.circleMarker([v.colegio_latitud, v.colegio_longitud], { radius: 5, color: '#198754', weight: 2, fillOpacity: 0.6 })
          .bindTooltip('Colegio: ' + this.escapar(v.lugar)).addTo(this.mapa);
        puntos.push([v.colegio_latitud, v.colegio_longitud]);
        if (v.lejos && v.latitud !== null) {
          L.polyline([[v.colegio_latitud, v.colegio_longitud], [v.latitud, v.longitud]], { color: '#dc3545', weight: 1.5, dashArray: '3 4' }).addTo(this.mapa);
        }
      }
      if (v.latitud === null) continue;
      const alerta = v.lejos || !!v.error;
      // Marcas en el mismo punto (colegios con la misma ubicación): el número se corre unos
      // píxeles en círculo, así se ven todos a cualquier zoom sin mover el punto real.
      const repetidas = marcadas.filter(p => Math.abs(p[0] - v.latitud) < 0.00005 && Math.abs(p[1] - v.longitud) < 0.00005).length;
      const angulo = repetidas * 2.4;
      const corrimiento = repetidas ? [Math.round(Math.cos(angulo) * 24), Math.round(Math.sin(angulo) * 24)] : [0, 0];
      const icono = L.divIcon({
        className: '',
        html: '<div class="marca-visita' + (alerta ? ' con-alerta' : '') + '" style="background:' + v.color + '">' + v.numero + '</div>',
        iconSize: [26, 26],
        iconAnchor: [13 - corrimiento[0], 13 - corrimiento[1]],
        popupAnchor: [corrimiento[0], corrimiento[1] - 10]
      });
      const marcador = L.marker([v.latitud, v.longitud], { icon: icono, zIndexOffset: 1000 })
        .bindPopup(this.contenidoPopup(v), { maxWidth: 280 })
        .addTo(this.mapa);
      this.marcadores[v.numero] = marcador;
      marcadas.push([v.latitud, v.longitud]);
      puntos.push([v.latitud, v.longitud]);
    }

    // El modal entra con animación: hasta que termina el contenedor no tiene su tamaño final.
    setTimeout(() => {
      this.mapa.invalidateSize();
      if (puntos.length) this.mapa.fitBounds(L.latLngBounds(puntos), { padding: [40, 40], maxZoom: 17 });
    }, 300);
  }

  private cargarGps() {
    const fecha = new Date(this.dia.fecha);
    const texto = fecha.getFullYear() + '-' + String(fecha.getMonth() + 1).padStart(2, '0') + '-' + String(fecha.getDate()).padStart(2, '0');
    this.usuarioService.buscarSeguimientoUsuarios(this.id_empresa, texto).subscribe((res: any) => {
      this.cargandoGps = false;
      const usuario = Array.isArray(res) ? res.find((u: any) => u.id === this.asesor.id_usuario) : null;
      const posiciones = (usuario?.posiciones || []).filter((p: any) => p.latitud !== null && p.longitud !== null);
      this.posicionesGps = posiciones.length;
      if (!posiciones.length) return;
      this.zone.runOutsideAngular(() => {
        const L = this.L;
        const linea = posiciones.map((p: any) => [parseFloat(p.latitud), parseFloat(p.longitud)] as [number, number]);
        L.polyline(linea, { color: '#6c757d', weight: 2, opacity: 0.6 }).addTo(this.capaGps);
        for (const p of posiciones) {
          L.circleMarker([parseFloat(p.latitud), parseFloat(p.longitud)], { radius: 2, color: '#6c757d', weight: 1, fillOpacity: 0.8 })
            .bindTooltip('GPS ' + this.hora(p.createdAt)).addTo(this.capaGps);
        }
      });
    }, () => this.cargandoGps = false);
  }

  irA(v: any) {
    const marcador = this.marcadores[v.numero];
    if (!marcador) return;
    this.zone.runOutsideAngular(() => {
      this.mapa.setView(marcador.getLatLng(), Math.max(this.mapa.getZoom(), 16));
      marcador.openPopup();
    });
  }

  private contenidoPopup(v: any): string {
    const lineas = [
      '<b>' + v.numero + '. ' + this.escapar(v.lugar) + '</b>',
      this.escapar(v.turno) + ' · ' + this.hora(v.llegada) + ' – ' + (this.hora(v.salida) || 'sin "Salí"'),
      v.minutos_lugar !== null ? v.minutos_lugar + ' min en el lugar' : '',
      v.minutos_traslado !== null ? v.minutos_traslado + ' min de traslado' + (v.minutos_hueco ? ' (' + v.minutos_hueco + ' de hueco)' : '') : '',
      v.distancia !== null ? '<span' + (v.lejos ? ' style="color:#dc3545"' : '') + '>A ' + v.distancia + ' m del colegio</span>' : '',
      v.error ? '<span style="color:#dc3545">' + this.escapar(v.error) + '</span>' : '',
      v.sin_conexion ? '<i>Marcado sin señal</i>' : '',
      v.observacion ? this.escapar(v.observacion) : ''
    ];
    return lineas.filter(Boolean).join('<br>');
  }

  hora(valor: any): string {
    if (!valor) return '';
    const d = new Date(valor);
    return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  }

  fecha(): string {
    const d = new Date(this.dia.fecha);
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return dias[d.getDay()] + ' ' + String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + d.getFullYear();
  }

  private escapar(texto: any): string {
    return String(texto ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}
